import { appState } from '../../../../../interfaces/common';
import {
  Button,
  ComboBox,
  Flex,
  Grid,
  Icon,
  Link,
  MenuGroupProps,
  menuHelpers,
  MenuItemProps,
  Option,
  Select,
  Switch,
  Text,
  Tooltip,
  useElement,
  useTheme,
} from '@pega/cosmos-react-core';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { RealTimeTranslationsLink } from '../../../../../constants/common';
import { languageMapping, languageMappingForTranslation } from '../../../../../constants/languageCodes';
import useLocalTranslation from '../../../../../hooks/useLocalTranslation';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../../redux/actions';
import CustomTooltip from '../../../../Utilities/CustomTooltip';
import { updateRealTimeTranslation } from '../../ChatApis';
import { useSupportedLanguagesSelector } from '../../../../../redux/selectors';

interface RealTimeTranslationProps {
  translationEnablerError: boolean;
  setTranslationEnablerError: Function;
  translateToLanguage?: string;
  connectionId: string;
  showPrimaryLanguage?: boolean;
  translationServiceId?: string;
  translateFromLanguages?: string[];
  languageTranslationEnabled?: boolean;
  showLink?: boolean;
}

const RealTimeTranslation: React.FC<RealTimeTranslationProps> = ({
  translationEnablerError,
  setTranslationEnablerError,
  translateToLanguage,
  connectionId,
  showPrimaryLanguage,
  translateFromLanguages = [],
  translationServiceId,
  languageTranslationEnabled,
  showLink,
}) => {
  const { encryptionEnabled } = useSelector((state: appState) => state.chatInfo);

  const { sessionId, configRecords } = useSelector((state: appState) => {
    return {
      accounts: state.accounts,
      sessionId: state.sessionId,
      configRecords: state.configRecords,
    };
  });

  const [serviceType, setServiceType] = useState('');
  const [enable, setEnable] = useState(languageTranslationEnabled);
  const [filterValue, setFilterValue] = useState('');
  const [showTooltipEl, setShowTooltipEl] = useElement();
  const [translateToLang, setTranslateToLang] = useState(translateToLanguage);
  const languagesSupportedByConsumer = useSupportedLanguagesSelector();

  useEffect(() => {
    if (translationServiceId && configRecords?.some((item) => item.connectionId === translationServiceId)) {
      setServiceType(translationServiceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationServiceId]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mapTranslateFromLanguages: { [key: string]: string } = translateFromLanguages?.reduce(
    (object: any, translateFromLanguage) => {
      object[translateFromLanguage] = translateFromLanguage;
      return object;
    },
    {},
  );

  const translateFromLanguageList: Array<MenuItemProps | MenuGroupProps> = Object.keys(
    languageMappingForTranslation,
  )?.reduce((languageList: Array<MenuItemProps | MenuGroupProps>, language: string) => {
    languageList.push({
      id: language,
      primary: languageMappingForTranslation[language],
      selected: !!mapTranslateFromLanguages[language],
    });
    return languageList;
  }, []);
  const [comboItems, setComboItems] = useState(translateFromLanguageList);
  const theme = useTheme();
  const translate = useLocalTranslation();

  const apiHandler = async (payload: any) => {
    dispatch(updateLoaderDetails(true));
    const json = await updateRealTimeTranslation(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('translation_service_update'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('translation_service_update_update_failed'), 'urgent'));
    }
  };

  const updateTranslateService = async () => {
    let translateFromLanguageLength = 0;
    const generateTranslateFromForDMS = selectedTranslateFromList.map((selectedItem) => {
      translateFromLanguageLength++;
      return selectedItem.id;
    });

    const payload: any = {
      translationServiceId: serviceType,
      translateFromLanguages: generateTranslateFromForDMS,
      languageTranslationEnabled: enable,
    };
    if (showPrimaryLanguage) {
      payload['primaryLanguage'] = translateToLang;
    }
    if (serviceType && translateFromLanguageLength > 0 && (translateToLanguage || translateToLang)) {
      await apiHandler(payload);
    }
  };

  const updateTranslationEnabler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const enable = event?.target?.checked;
    if (!enable) {
      const payload = {
        languageTranslationEnabled: enable,
      };
      await apiHandler(payload);
    }
    if (!translateToLang || !serviceType || selectedTranslateFromLength <= 0) {
      setTranslationEnablerError(true);
    }
    setEnable(enable);
  };

  const handleComboBoxInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event?.target?.value);
  };

  const handleComboInputBlur = () => {
    setFilterValue('');
  };

  const serviceHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const data = e.target.value;
    if (data === 'empty') {
      return;
    }
    setServiceType(data);
  };

  const getFilterRegex = (inputValue: string) => {
    return new RegExp(`^${inputValue.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&')}`, 'i');
  };

  const filterRegex = React.useMemo(() => getFilterRegex(filterValue), [filterValue]);

  const itemsToRender = useMemo(() => {
    const newItems = filterValue
      ? menuHelpers.flatten(comboItems).filter(({ primary }) => {
          return filterRegex.test(primary);
        })
      : comboItems;
    return menuHelpers.mapTree(newItems, (item) => {
      return {
        ...item,
        selected: item.items ? undefined : !!item.selected,
      };
    });
  }, [filterValue, comboItems, filterRegex]);

  const toggleTranslateFromItem = (id: string) => {
    setFilterValue('');
    setComboItems((prevComboItems) => menuHelpers.toggleSelected(prevComboItems, id, 'multi-select'));
  };

  const selectedTranslateFromList = useMemo(() => {
    return menuHelpers.getSelected(comboItems).map(({ primary, id }) => ({ text: primary, id: id }));
  }, [comboItems]);

  const selectedTranslateFromLength = comboItems.length;
  const showTranslateFromError =
    (translationEnablerError && selectedTranslateFromLength < 1) ||
    (languageTranslationEnabled && selectedTranslateFromLength < 1);

  const updateTranslateToLang = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const data = e.target.value;
    if (data === 'empty') {
      return;
    }
    setTranslateToLang(data);
  };

  const enableSaveButton = translateToLanguage
    ? Boolean(serviceType && translateToLanguage && selectedTranslateFromList.length > 0)
    : Boolean(serviceType && translateToLang && selectedTranslateFromList.length > 0);

  return (
    <>
      <Flex container={{ direction: 'column', itemGap: 1.5 }} style={{ width: '70%' }}>
        <Text variant="h3">{translate('real_time_translations')}</Text>
        {showLink && (
          <Link href={RealTimeTranslationsLink} target="_blank" variant="link">
            {translate('real_time_translation_link_label')}
            <Icon name="open" />
          </Link>
        )}
        <Grid container={{ alignItems: 'start' }}>
          <Text
            variant="h4"
            style={{
              alignSelf: 'center',
              color: encryptionEnabled ? theme.base.colors.gray.dark : theme.base.colors.black,
            }}
          >
            {translate('enable_language_translation')}{' '}
          </Text>
          <Flex container={{ inline: true }} style={{ marginLeft: '-4px' }} ref={setShowTooltipEl}>
            <Switch
              name="isEnabled"
              onChange={updateTranslationEnabler}
              labelHidden={true}
              on={enable}
              disabled={encryptionEnabled}
            />
            {encryptionEnabled && <Tooltip target={showTooltipEl}>{translate('byok_3rd_party_lang_info')}</Tooltip>}
          </Flex>
        </Grid>
        {enable && (
          <>
            <Grid
              container={{
                gap: 1,
              }}
            >
              <Text>{translate('translation_service')} :</Text>
              <Flex>
                <Select
                  value={serviceType}
                  onChange={serviceHandler}
                  status={
                    (translationEnablerError && !serviceType) || (languageTranslationEnabled && !serviceType)
                      ? 'error'
                      : undefined
                  }
                >
                  <Option key="uniquesKey" value="empty">
                    {translate('service_selection')}
                  </Option>
                  {configRecords?.map((item) => (
                    <Option value={item.connectionId} key={item.connectionId}>
                      {item.accountName}
                    </Option>
                  ))}
                </Select>
                {((translationEnablerError && !serviceType) || (languageTranslationEnabled && !serviceType)) && (
                  <Text status="error">{translate('service_selection')}</Text>
                )}
              </Flex>
            </Grid>
            {showPrimaryLanguage && (
              <Grid
                container={{
                  cols: '33%',
                  gap: 1,
                  alignItems: 'end',
                }}
              >
                <Text>{translate('primary_lang_csr')} :</Text>
                <Select
                  value={translateToLang}
                  onChange={updateTranslateToLang}
                  status={translationEnablerError && !translateToLang ? 'error' : undefined}
                >
                  <Option key="uniquesKey2" value="empty">
                    {translate('primary_lang_selection')}
                  </Option>
                  {languagesSupportedByConsumer?.reduce((translateToLanguageList: any, language: any) => {
                    if (languageMapping[language]) {
                      translateToLanguageList.push(
                        <Option value={language} key={language}>
                          {languageMapping[language]}
                        </Option>,
                      );
                    }
                    return translateToLanguageList;
                  }, [])}
                </Select>
                {translationEnablerError && !translateToLang && (
                  <Text status="error">{translate('primary_lang_selection')}</Text>
                )}
              </Grid>
            )}
            <Grid
              container={{
                gap: 1,
              }}
            >
              <Text>
                {translate('translate_from')}
                <CustomTooltip
                  info={translate('translate_from_tooltip')}
                  iconName="help"
                  tooltipStyle={{ marginLeft: '5px' }}
                />
              </Text>
              <Flex container={{ direction: 'column', itemGap: 1.5 }}>
                <ComboBox
                  mode="multi-select"
                  label={translate('translate_from')}
                  labelHidden={true}
                  required={true}
                  placeholder={''}
                  status={showTranslateFromError ? 'error' : undefined}
                  selected={{
                    items: selectedTranslateFromList,
                    onRemove: toggleTranslateFromItem,
                  }}
                  value={filterValue}
                  onChange={handleComboBoxInputChange}
                  onBlur={handleComboInputBlur}
                  menu={{
                    items: itemsToRender,
                    onItemClick: toggleTranslateFromItem,
                    accent: filterRegex,
                    emptyText: translate('no_items'),
                    scrollAt: 10,
                  }}
                />
                {showTranslateFromError && <Text status="error">{translate('translate_from_lang_error')}</Text>}
              </Flex>
            </Grid>
            <Flex
              container={{
                direction: 'column',
                alignItems: 'start',
                itemGap: 1,
              }}
            >
              <Button onClick={updateTranslateService} disabled={!enableSaveButton} variant="primary">
                {translate('save')}
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </>
  );
};

export default RealTimeTranslation;
